import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  PlusIcon, 
  CalendarIcon, 
  CheckCircleIcon,
  XCircleIcon,
  TrophyIcon,
  FireIcon,
  UserIcon,
  LockOpenIcon,
  LockClosedIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';
import { SeasonService, ContestantService, PredictionService } from '../../../services/firebaseService';
import { ScoreService } from '../../../services/scoreService';
import {
  isMultiEliminationWeek,
  isSimultaneousDoubleEliminationWeek,
} from '../../../utils/predictionUtils';
import { ELIMINATION_TYPES } from '../../../models/dataModels';

export default function WeekManagement() {
  const { seasonId } = useParams();
  const navigate = useNavigate();
  const [season, setSeason] = useState(null);
  const [contestants, setContestants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateWeekModal, setShowCreateWeekModal] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [openWeeksCount, setOpenWeeksCount] = useState(0);
  const [weekPredictionCounts, setWeekPredictionCounts] = useState({});

  useEffect(() => {
    loadSeasonData();
  }, [seasonId]);

  useEffect(() => {
    if (season && season.weeks) {
      // Count open weeks
      const openCount = season.weeks.filter(w => w.isOpenForPredictions && !w.results).length;
      setOpenWeeksCount(openCount);
    }
  }, [season]);

  useEffect(() => {
    if (season && season.weeks && seasonId) {
      // Load prediction counts for all weeks
      loadPredictionCounts();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [season, seasonId]);

  const loadSeasonData = async () => {
    try {
      setLoading(true);
      const [seasonData, contestantsData] = await Promise.all([
        SeasonService.getSeason(seasonId),
        ContestantService.getAllContestants()
      ]);
      
      setSeason(seasonData);
      setContestants(contestantsData);
    } catch (error) {
      console.error('Error loading season data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPredictionCounts = async () => {
    if (!season || !season.weeks) return;
    
    try {
      const counts = {};
      for (const week of season.weeks) {
        if (week.isOpenForPredictions && !week.results) {
          const predictions = await PredictionService.getPredictionsBySeasonAndWeek(
            seasonId,
            week.weekNumber
          );
          // Count only unlocked predictions
          const activeCount = predictions.filter(p => !p.isLocked).length;
          counts[week.weekNumber] = activeCount;
        }
      }
      setWeekPredictionCounts(counts);
    } catch (error) {
      console.error('Error loading prediction counts:', error);
    }
  };

  const getContestantName = (contestantId) => {
    const contestant = contestants.find(c => c.id === contestantId);
    return contestant ? contestant.name : 'Unknown Contestant';
  };

  const getWeekStatus = (week) => {
    if (week.results) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <CheckCircleIcon className="h-3 w-3 mr-1" />
          Results Entered
        </span>
      );
    }
    if (week.isOpenForPredictions) {
      const predictionCount = weekPredictionCounts[week.weekNumber] || 0;
      return (
        <span 
          className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
          title={`Open for predictions • ${predictionCount} active prediction${predictionCount !== 1 ? 's' : ''}`}
        >
          <LockOpenIcon className="h-3 w-3 mr-1" />
          Open for Predictions
          {predictionCount > 0 && (
            <span className="ml-1.5 px-1.5 py-0.5 rounded bg-blue-200 text-blue-900 font-semibold">
              {predictionCount}
            </span>
          )}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        <LockClosedIcon className="h-3 w-3 mr-1" />
        Draft
      </span>
    );
  };

  const handleCreateWeek = async (weekData) => {
    try {
      const newWeek = {
        weekNumber: (season.weeks?.length || 0) + 1,
        ...weekData,
        isOpenForPredictions: false,
        results: null
      };

      const updatedWeeks = [...(season.weeks || []), newWeek];
      await SeasonService.updateSeason(seasonId, { weeks: updatedWeeks });
      
      setSeason({ ...season, weeks: updatedWeeks });
      setShowCreateWeekModal(false);
      // Reload prediction counts after creating week
      setTimeout(() => loadPredictionCounts(), 500);
    } catch (error) {
      console.error('Error creating week:', error);
    }
  };

  const handleOpenPredictions = async (weekIndex) => {
    try {
      const updatedWeeks = [...season.weeks];
      updatedWeeks[weekIndex].isOpenForPredictions = true;
      
      await SeasonService.updateSeason(seasonId, { weeks: updatedWeeks });
      setSeason({ ...season, weeks: updatedWeeks });
      // Reload prediction counts after opening predictions
      setTimeout(() => loadPredictionCounts(), 500);
    } catch (error) {
      console.error('Error opening predictions:', error);
    }
  };

  const handleEnterResults = async (weekIndex, results) => {
    try {
      const updatedWeeks = [...season.weeks];
      updatedWeeks[weekIndex].results = results;
      updatedWeeks[weekIndex].isOpenForPredictions = false;
      
      await SeasonService.updateSeason(seasonId, { weeks: updatedWeeks });
      setSeason({ ...season, weeks: updatedWeeks });
      
      // Trigger score recalculation
      const week = updatedWeeks[weekIndex];
      await ScoreService.recalculateScoresForSpecificWeek(
        seasonId,
        week.weekNumber,
        results,
        week.isFinale
      );
      
      setShowResultsModal(false);
      setSelectedWeek(null);
    } catch (error) {
      console.error('Error entering results:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-survivor-500"></div>
      </div>
    );
  }

  if (!season) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900">Season not found</h2>
          <p className="text-gray-500">The season you're looking for doesn't exist.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="px-4 py-6 sm:px-0">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Week Management</h1>
              <p className="mt-1 text-sm text-gray-500">
                {season.name} - Manage weeks and results
              </p>
              {openWeeksCount > 0 && (
                <div className="mt-2 inline-flex items-center px-3 py-1.5 rounded-md bg-blue-50 border border-blue-200">
                  <InformationCircleIcon className="h-4 w-4 text-blue-600 mr-2" />
                  <span className="text-sm font-medium text-blue-900">
                    {openWeeksCount} week{openWeeksCount !== 1 ? 's' : ''} currently open for predictions
                  </span>
                </div>
              )}
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowCreateWeekModal(true)}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
              >
                <PlusIcon className="h-4 w-4 mr-2" />
                Add Week
              </button>
            </div>
          </div>
        </div>

        {/* Weeks List */}
        <div className="px-4 py-6 sm:px-0">
          <div className="bg-white shadow overflow-hidden sm:rounded-md">
            {season.weeks && season.weeks.length > 0 ? (
              <ul className="divide-y divide-gray-200">
                {season.weeks.map((week, index) => (
                  <li key={index}>
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div className={`h-10 w-10 rounded-full flex items-center justify-center ${
                              week.isOpenForPredictions && !week.results
                                ? 'bg-blue-100 ring-2 ring-blue-300'
                                : week.results
                                ? 'bg-green-100'
                                : 'bg-gray-100'
                            }`}>
                              {week.isOpenForPredictions && !week.results ? (
                                <LockOpenIcon className="h-6 w-6 text-blue-600" />
                              ) : week.results ? (
                                <CheckCircleIcon className="h-6 w-6 text-green-600" />
                              ) : (
                                <CalendarIcon className="h-6 w-6 text-gray-600" />
                              )}
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="flex items-center">
                              <h3 className="text-lg font-medium text-gray-900">
                                Week {week.weekNumber}
                                {week.isFinale && (
                                  <span className="ml-2 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                    <TrophyIcon className="h-3 w-3 mr-1" />
                                    Finale
                                  </span>
                                )}
                              </h3>
                              <div className="ml-2">{getWeekStatus(week)}</div>
                            </div>
                            <div className="mt-1 text-sm text-gray-500">
                              {week.isFinale ? 'Final Tribal Council' : 'Regular Episode'}
                            </div>
                            {week.results && (
                              <div className="mt-2 text-sm text-gray-600">
                                {week.isFinale ? (
                                  <div>
                                    <span className="font-medium">Winner:</span> {getContestantName(week.results.winner)} • 
                                    <span className="font-medium ml-2">Runner-up:</span> {getContestantName(week.results.runnerUp)} • 
                                    <span className="font-medium ml-2">Fire-making loser:</span> {getContestantName(week.results.fireMakingLoser)}
                                  </div>
                                ) : isMultiEliminationWeek(week) && Array.isArray(week.results.votedOut) && week.results.votedOut.length > 0 && week.results.votedOut[0].position ? (
                                  <div>
                                    <span className="font-medium">Voted out:</span>
                                    <div className="ml-2 mt-1 space-y-1">
                                      {week.results.votedOut
                                        .sort((a, b) => a.position - b.position)
                                        .map(item => (
                                          <div key={`${item.position}-${item.contestantId || item.contestantName || ''}`} className="text-xs">
                                            <span className="font-medium">Prediction #{item.position}:</span> {getContestantName(item.contestantId || item.contestantName)}
                                          </div>
                                        ))}
                                    </div>
                                  </div>
                                ) : (
                                  <div>
                                    <span className="font-medium">Voted out:</span> {Array.isArray(week.results.votedOut) ? week.results.votedOut.map(id => getContestantName(id)).join(', ') : getContestantName(week.results.votedOut)}
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          {!week.isOpenForPredictions && !week.results && (
                            <button
                              onClick={() => handleOpenPredictions(index)}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200"
                            >
                              Open Predictions
                            </button>
                          )}
                          {!week.results && (
                            <button
                              onClick={() => {
                                setSelectedWeek({ ...week, index });
                                setShowResultsModal(true);
                              }}
                              className="inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200"
                            >
                              Enter Results
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="text-center py-12">
                <CalendarIcon className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">No weeks created</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Get started by creating the first week for this season.
                </p>
                <div className="mt-6">
                  <button
                    onClick={() => setShowCreateWeekModal(true)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add First Week
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Create Week Modal */}
        {showCreateWeekModal && (
          <CreateWeekModal
            onClose={() => setShowCreateWeekModal(false)}
            onCreateWeek={handleCreateWeek}
          />
        )}

        {/* Enter Results Modal */}
        {showResultsModal && selectedWeek && (
          <EnterResultsModal
            week={selectedWeek}
            contestants={contestants}
            onClose={() => {
              setShowResultsModal(false);
              setSelectedWeek(null);
            }}
            onEnterResults={handleEnterResults}
          />
        )}
      </div>
    </div>
  );
}

// Create Week Modal Component
function CreateWeekModal({ onClose, onCreateWeek }) {
  const [isFinale, setIsFinale] = useState(false);
  const [description, setDescription] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    onCreateWeek({
      isFinale,
      description: description.trim() || null
    });
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Create New Week</h3>
          
          <form onSubmit={handleSubmit}>
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isFinale"
                  checked={isFinale}
                  onChange={(e) => setIsFinale(e.target.checked)}
                  className="h-4 w-4 text-survivor-600 focus:ring-survivor-500 border-gray-300 rounded"
                />
                <label htmlFor="isFinale" className="ml-2 block text-sm text-gray-900">
                  This is a finale week
                </label>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700">Description (Optional)</label>
                <textarea
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                  placeholder="Brief description of the episode"
                  rows={3}
                />
              </div>
            </div>
            
            <div className="mt-6 flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
              >
                Create Week
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

// Enter Results Modal Component
function EnterResultsModal({ week, contestants, onClose, onEnterResults }) {
  const [results, setResults] = useState({
    votedOut: [],
    votedOutOrdered: [], // For double eliminations - ordered array
    winner: '',
    runnerUp: '',
    fireMakingLoser: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    const finalResults = week.isFinale 
      ? { winner: results.winner, runnerUp: results.runnerUp, fireMakingLoser: results.fireMakingLoser }
      : isMultiEliminationWeek(week)
        ? { votedOut: results.votedOutOrdered }
        : { votedOut: results.votedOut };
    
    onEnterResults(week.index, finalResults);
  };

  const toggleVotedOut = (contestantId) => {
    setResults(prev => ({
      ...prev,
      votedOut: prev.votedOut.includes(contestantId)
        ? prev.votedOut.filter(id => id !== contestantId)
        : [...prev.votedOut, contestantId]
    }));
  };

  const handleDoubleEliminationOrder = (contestantId, position) => {
    setResults(prev => {
      const newOrdered = [...prev.votedOutOrdered];
      
      // Remove contestant from any existing position
      const existingIndex = newOrdered.findIndex(item => item.contestantId === contestantId);
      if (existingIndex !== -1) {
        newOrdered.splice(existingIndex, 1);
      }
      
      // Add contestant at new position
      if (position > 0) {
        newOrdered[position - 1] = { contestantId, position };
      }
      
      return { ...prev, votedOutOrdered: newOrdered };
    });
  };

  const getContestantName = (contestantId) => {
    const contestant = contestants.find(c => c.id === contestantId);
    return contestant ? contestant.name : 'Unknown Contestant';
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
      <div className="relative top-10 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
        <div className="mt-3">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Enter Results for Week {week.weekNumber}
            {week.isFinale && <span className="ml-2 text-yellow-600">(Finale)</span>}
          </h3>
          
          <form onSubmit={handleSubmit}>
            {week.isFinale ? (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Winner</label>
                  <select
                    value={results.winner}
                    onChange={(e) => setResults({ ...results, winner: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                    required
                  >
                    <option value="">Select winner</option>
                    {contestants.map(contestant => (
                      <option key={contestant.id} value={contestant.id}>
                        {contestant.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Runner-up</label>
                  <select
                    value={results.runnerUp}
                    onChange={(e) => setResults({ ...results, runnerUp: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                    required
                  >
                    <option value="">Select runner-up</option>
                    {contestants.map(contestant => (
                      <option key={contestant.id} value={contestant.id}>
                        {contestant.name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700">Fire-making loser</label>
                  <select
                    value={results.fireMakingLoser}
                    onChange={(e) => setResults({ ...results, fireMakingLoser: e.target.value })}
                    className="mt-1 block w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                    required
                  >
                    <option value="">Select fire-making loser</option>
                    {contestants.map(contestant => (
                      <option key={contestant.id} value={contestant.id}>
                        {contestant.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            ) : isMultiEliminationWeek(week) ? (
              <div>
                <div className="mb-4 p-3 bg-purple-50 border border-purple-200 rounded-md">
                  <div className="flex items-center">
                    <FireIcon className="h-5 w-5 text-purple-600 mr-2" />
                    <span className="text-sm font-medium text-purple-800">
                      {week.eliminationType === ELIMINATION_TYPES.TRIPLE
                        ? 'Triple Elimination Week'
                        : isSimultaneousDoubleEliminationWeek(week)
                          ? 'Double Elimination (Simultaneous) Week'
                          : 'Double Elimination Week'}
                    </span>
                  </div>
                  <p className="text-xs text-purple-600 mt-1">
                    {isSimultaneousDoubleEliminationWeek(week)
                      ? 'Positions #1 and #2 match prediction order. No survival for booted players; two survival increments for drafted players still in the game.'
                      : `Select elimination order: #1 first out, #2 second${
                          week.eliminationType === ELIMINATION_TYPES.TRIPLE
                            ? ', #3 third.'
                            : '.'
                        }`}
                  </p>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prediction #1 (First Voted Out)
                    </label>
                    <select
                      value={results.votedOutOrdered.find(item => item.position === 1)?.contestantId || ''}
                      onChange={(e) => handleDoubleEliminationOrder(e.target.value, 1)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                    >
                      <option value="">Select first person voted out</option>
                      {contestants.map(contestant => (
                        <option key={contestant.id} value={contestant.id}>
                          {contestant.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Prediction #2 (Second Voted Out)
                    </label>
                    <select
                      value={results.votedOutOrdered.find(item => item.position === 2)?.contestantId || ''}
                      onChange={(e) => handleDoubleEliminationOrder(e.target.value, 2)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                    >
                      <option value="">Select second person voted out</option>
                      {contestants.map(contestant => (
                        <option key={contestant.id} value={contestant.id}>
                          {contestant.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  {week.eliminationType === ELIMINATION_TYPES.TRIPLE && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Prediction #3 (Third Voted Out)
                      </label>
                      <select
                        value={results.votedOutOrdered.find(item => item.position === 3)?.contestantId || ''}
                        onChange={(e) => handleDoubleEliminationOrder(e.target.value, 3)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-survivor-500 focus:border-survivor-500"
                      >
                        <option value="">Select third person voted out</option>
                        {contestants.map(contestant => (
                          <option key={contestant.id} value={contestant.id}>
                            {contestant.name}
                          </option>
                        ))}
                      </select>
                    </div>
                  )}

                  {results.votedOutOrdered.length > 0 && (
                    <div className="mt-4 p-3 bg-gray-50 border border-gray-200 rounded-md">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Current Selections:</h4>
                      <div className="space-y-1">
                        {results.votedOutOrdered
                          .filter(item => item && item.contestantId)
                          .sort((a, b) => a.position - b.position)
                          .map(item => (
                            <div key={`${item.position}-${item.contestantId}`} className="text-sm text-gray-600">
                              <span className="font-medium">Prediction #{item.position}:</span> {getContestantName(item.contestantId)}
                            </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Voted Out Contestants</label>
                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto">
                  {contestants.map(contestant => (
                    <label key={contestant.id} className="flex items-center p-2 border rounded hover:bg-gray-50">
                      <input
                        type="checkbox"
                        checked={results.votedOut.includes(contestant.id)}
                        onChange={() => toggleVotedOut(contestant.id)}
                        className="h-4 w-4 text-survivor-600 focus:ring-survivor-500 border-gray-300 rounded"
                      />
                      <span className="ml-2 text-sm">{contestant.name}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
            
            <div className="mt-6 flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-survivor-600 hover:bg-survivor-700"
              >
                Save Results
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
